import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import {SetURLSearchParams} from "react-router-dom";
import { CURRENCY_OPTIONS } from '../types/constants';


export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}


export function getCurrencySymbol(currencyCode: string): string {
    const upper = currencyCode.trim().toUpperCase();
    const currency = CURRENCY_OPTIONS.find(c => c.value === upper);
    return currency?.symbol || upper;
}
export function sortImmutable<T>(arr: T[], sortFn: (a: T, b: T) => number): T[] {
    const newArr = arr.slice()
    newArr.sort(sortFn)
    return newArr
}

export function useUrlState<T>(params: URLSearchParams, setParams: SetURLSearchParams, serializeFn: (params: URLSearchParams) => T, deserializeFn: (newState: T) => URLSearchParams): [T, (T) => void] {
    function getState() {
        return serializeFn(params)
    }

    function updateState(newState: T) {
        const newParams = deserializeFn(newState)
        setParams(newParams)
    }

    return [getState(), updateState]
}

export function capIfLong (str: string, maxLength: number = 22)  {
    return str.length > maxLength ? str.slice(0, maxLength) + '…' : str;
}

export function formatYAxisTick(value: number): string {
    if (!isFinite(value) || isNaN(value)) {
        return '0';
    }

    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (absValue >= 1000000000) {
        return `${sign}${(absValue / 1000000000).toFixed(1)}B`;
    } else if (absValue >= 1000000) {
        return `${sign}${(absValue / 1000000).toFixed(1)}M`;
    } else if (absValue >= 1000) {
        return `${sign}${(absValue / 1000).toFixed(0)}k`;
    }
    return Number.isInteger(value) ? value.toString() : value.toFixed(1);
}

export type PieChartCategory = {
    name: string;
    percentage: number;
    amount: number;
    icon: string | null;
};

export type PieChartBreakdownItem = {
    percentage: number;
    amount: number;
    icon?: string | null;
};

export function breakdownToChartData(
    data: Record<string, PieChartBreakdownItem>,
): PieChartCategory[] {
    return Object.entries(data).map(([name, details]) => ({
        name,
        percentage: details.percentage,
        amount: details.amount / 100,
        icon: details.icon ?? null,
    }));
}

export type PeriodOption = 'current_year' | 'year' | 'quarter' | 'month';

export function getDateRangeForPeriod(
    period: PeriodOption,
    params: {
        selectedYear: number;
        selectedMonth: number;
        selectedQuarterYear: { year: number; quarter?: number };
        dateFrom?: string;
        dateTo?: string;
    }
): { from: string; to: string } {
    const { selectedYear, selectedMonth, selectedQuarterYear, dateFrom, dateTo } = params;
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const currentDay = now.getDate();

    switch (period) {
        case 'current_year':
            return {
                from: `${currentYear}-01-01`,
                to: `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(currentDay).padStart(2, '0')}`,
            };
        case 'year':
            return {
                from: `${selectedYear}-01-01`,
                to: `${selectedYear}-12-31`,
            };
        case 'quarter': {
            const quarter = selectedQuarterYear.quarter || 1;
            const year = selectedQuarterYear.year;
            const quarterStartMonth = (quarter - 1) * 3 + 1;
            const quarterEndMonth = quarter * 3;
            const daysInMonth = new Date(year, quarterEndMonth, 0).getDate();
            return {
                from: `${year}-${String(quarterStartMonth).padStart(2, '0')}-01`,
                to: `${year}-${String(quarterEndMonth).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`,
            };
        }
        case 'month': {
            const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
            return {
                from: `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`,
                to: `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`,
            };
        }
        default:
            return {
                from: dateFrom || `${currentYear}-01-01`,
                to: dateTo || `${currentYear}-12-31`,
            };
    }
}

export function formatDate(isoDate: string | null | undefined, options: Intl.DateTimeFormatOptions = { day: "2-digit", month: "2-digit", year: "numeric" }): string {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString("nl-NL", options);
}

export function formatDateTime(isoDateTime: string): string {
    return new Date(isoDateTime).toLocaleString("nl-NL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}


